import { IOGateway } from '@interopio/gateway';
import type { Node, NodeConnection, NodeConnections, NodeEventTypes } from './types.ts';
import { EventEmitter } from 'node:events';

type NodeValue = Omit<Node, 'users'> & {
    node: string,
    users: Set<string>,
    lastAccess: number
}

class OrderedMap<T> {
    readonly #elements: Array<[string, T]> = [];

    #binarySearch(key: string): number {
        let low = 0;
        let high = this.#elements.length - 1;
        while (low <= high) {
            const mid = (low + high) >> 1;
            const cmp = this.#elements[mid][0].localeCompare(key);
            if (cmp === 0) return mid;
            if (cmp < 0) low = mid + 1;
            else high = mid - 1;
        }
        return -low - 1; // not found, return insertion point
    }

    set(key: string, value: T) {
        const idx = this.#binarySearch(key);
        if (idx >= 0) {
            this.#elements[idx][1] = value;
        } else {
            this.#elements.splice(~idx, 0, [key, value]);
        }
    }

    get(key: string): T | undefined {
        const idx = this.#binarySearch(key);
        return idx >= 0 ? this.#elements[idx][1] : undefined;
    }

    has(key: string): boolean {
        return this.#binarySearch(key) >= 0;
    }

    delete(key: string): boolean {
        const idx = this.#binarySearch(key);
        if (idx >= 0) {
            this.#elements.splice(idx, 1);
            return true;
        }
        return false;
    }

    entries(): IterableIterator<[string, T]> {
        return this.#elements[Symbol.iterator]();
    }

    values(): T[] {
        return this.#elements.map((e) => e[1]);
    }
}

export default class InMemoryNodeConnections implements NodeConnections {
    readonly #logger: IOGateway.Logging.Logger;
    readonly #eventEmitter = new EventEmitter<NodeEventTypes>();
    readonly #nodes = new OrderedMap<NodeValue>();
    readonly #nodesByEndpoint = new Map<string, string>();
    readonly #timeout: number;

    constructor(logger: IOGateway.Logging.Logger,
                timeout: number = 60000) {
        this.#logger = logger;
        this.#timeout = timeout;
    }

    announce(nodes: Node[]): NodeConnection[] {
        const now = Date.now();
        for (const node of nodes) {
            const {node: nodeId, users, endpoint} = node;
            const foundId: string | undefined = this.#nodesByEndpoint.get(endpoint);
            if (foundId) {
                if (foundId !== nodeId) {
                    this.#logger.warn(`endpoint ${endpoint} clash. replacing node ${foundId} with ${nodeId}`);
                    this.#nodesByEndpoint.set(endpoint, nodeId);
                    this.#nodes.delete(foundId);
                    this.#eventEmitter.emit('endpoint-deleted', foundId, endpoint, 'clashed');
                }
            } else {
                this.#logger.info(`endpoint ${endpoint} announced for ${nodeId}`);
                this.#nodesByEndpoint.set(endpoint, nodeId);
            }
            const set = new Set<string>(users ?? []);
            this.#nodes.set(nodeId, this.updateNode(node, set, nodeId, now, this.#nodes.get(nodeId)));
            this.#eventEmitter.emit('endpoint-announced', nodeId, endpoint, set);
        }
        this.cleanupOldNodes();
        return nodes.map((e) => {
            const {node} = e;
            const connect = this.findConnections(this.#nodes.get(node));
            return {node, connect};
        });
    }

    find(nodeId: string): Node[] | undefined {
        const e = this.#nodes.get(nodeId);
        if (e !== undefined) {
            return this.findConnections(e);
        }
        return undefined;
    }

    remove(nodeId: string) {
        const removed = this.#nodes.get(nodeId);
        if (removed !== undefined) {
            this.#nodes.delete(nodeId);
            const endpoint = removed.endpoint;
            this.#nodesByEndpoint.delete(endpoint);
            this.#logger.info(`endpoint ${endpoint} removed for ${nodeId}`);
            this.#eventEmitter.emit('endpoint-deleted', nodeId, endpoint, 'removed');
            return true;
        }
        return false;
    }

    private updateNode(newNode: Node, users: Set<string>, _key: string, lastAccess: number, oldNode?: NodeValue): NodeValue {
        const node: Omit<NodeValue, 'lastAccess' | 'users'> = oldNode ?? {...newNode };
        return {...node, users, lastAccess};
    }

    private cleanupOldNodes() {
        const threshold = Date.now() - this.#timeout;
        for (const [nodeId,v] of this.#nodes.entries()) {
            if (v.lastAccess < threshold) {
                if (this.#logger.enabledFor('debug')) {
                    this.#logger.debug(`${nodeId} expired - no announcement since ${new Date(v.lastAccess).toISOString()}, timeout is ${this.#timeout} ms.`);
                }
                this.#nodes.delete(nodeId);
                const endpoint = v.endpoint;
                this.#nodesByEndpoint.delete(endpoint);
                this.#eventEmitter.emit('endpoint-deleted', nodeId, endpoint, 'expired');
            }
        }
    }

    public on<K extends keyof NodeEventTypes>(event: K, listener: (K extends keyof NodeEventTypes ? NodeEventTypes[K] extends unknown[] ? (...args: NodeEventTypes[K]) => void : never : never)): this {
        this.#eventEmitter.on(event, listener);
        return this;
    }

    public off<K extends keyof NodeEventTypes>(event: K, listener: (K extends keyof NodeEventTypes ? NodeEventTypes[K] extends unknown[] ? (...args: NodeEventTypes[K]) => void : never : never)): this {
        this.#eventEmitter.off(event, listener);
        return this;
    }

    private findConnections(node?: NodeValue): Node[] {
        const connections = new Array<Node>();
        for (const [key, value] of this.#nodes.entries()) {
            if (node !== undefined && key < node.node) {
                const intersection = new Set(value.users);
                node.users.forEach(user => {
                    if (!value.users.has(user)) {
                        intersection.delete(user);
                    }
                });
                value.users.forEach(user => {
                    if (!node.users.has(user)) {
                        intersection.delete(user);
                    }
                });
                if (intersection.size > 0) {
                    const e: Node = {node: value.node, endpoint: value.endpoint};
                    connections.push(e);
                }
            }
        }
        return connections;
    }
}
